// File created by Reginald Sahil Chand on 26-03-2024.

import React, {type ReactNode, type ReactElement} from 'react';

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand@gmail.com
 *
 * @property {ReactNode?} buttonIcon - Takes an icon as the property. Example <MdMoon /> (This property is optional).
 * @property {string?} buttonWidth - Takes a string which represents the elements width. (This property is optional).
 * @property {string?} buttonHeight - Takes a string which represents the elements height. (This property is optional).
 * @property {string?} buttonText - Takes a string which represents the elements text. (This property is optional).
 * @property {string} buttonDescription - Takes a string which represents the elements title and aria-label.
 * 					   (This property is not optional, by default if no description is provided, then the users will see a
 * 					   default description set by the developer).
 * @property {void?} buttonFunction - Takes a void function which will be provided by the developer, in-order to perform certain,
 * 					tasks. Example switching themes or opening and closing of navigation drawers. (This property is optional).
 *
 * @version 0.1.0
 */
type ButtonProps = {
	buttonIcon?: ReactNode;
	buttonWidth?: string;
	buttonHeight?: string;
	buttonText?: string;
	buttonDescription: string;
	buttonFunction?: () => void;
};

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand@gmail.com
 *
 * @description Button component responsible of displaying all kinds of buttons, whether it is text based, icon based or both.
 * @summary In-order to display this button component, the developer must provide the buttonDescription. Rest of the properties,
 * 			are optional.
 * @see {ButtonProps}
 * @type {ButtonProps}
 * @version 0.1.0
 *
 * @return {JSX Element} Button component.
 */
export const Button: React.FC<ButtonProps> = React.memo(
	({
		buttonIcon,
		buttonWidth,
		buttonHeight,
		buttonText,
		buttonDescription,
		buttonFunction,
	}: ButtonProps): ReactElement => {
		const initialButtonWidth: string = (buttonWidth && buttonWidth) ?? 'w-[3.6rem]';
		const initialButtonHeight: string = (buttonHeight && buttonHeight) ?? 'h-[3.6rem]';
		const initialButtonDescription: string =
			(buttonDescription && buttonDescription) ?? 'Button Title Is Not Specified';

		return (
			<button
				className={`${initialButtonWidth} ${initialButtonHeight} flex items-center justify-center rounded-xl bg-secondary transition-colors duration-300 ease-in-out hover:bg-primary`}
				type="button"
				title={initialButtonDescription}
				aria-label={initialButtonDescription}
				tabIndex={0}
				onClick={buttonFunction}
			>
				<div>
					<div className="scale-125 text-[white]">{buttonIcon && buttonIcon}</div>
					<p className="text-[white]">{buttonText && buttonText}</p>
				</div>
			</button>
		);
	},
);

Button.displayName = 'Button';
